import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail, MapPin, MessageCircle, Phone, Upload as UploadIcon } from "lucide-react";
import { PageHero } from "./ContentPage";
import { Reveal } from "../components/Motion";
import { Btn, Container } from "../components/Shared";
import { WA, PHONE } from "../components/Footer";
import { api, errDetail } from "../lib/api";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";

export const Field = ({ label, error, children, id }) => (
  <label className="block" htmlFor={id}>
    <span className="mb-2 block text-sm font-medium text-[var(--ink)]">{label}</span>
    {children}
    {error && <span className="mt-1.5 block text-sm text-[#B42318]" role="alert" data-testid={`${id}-error`}>{error}</span>}
  </label>
);
export const inputCls = "w-full rounded-sm border border-[var(--line)] bg-white px-4 py-3.5 text-[var(--ink)] outline-none transition-[border-color,box-shadow] duration-300 focus:border-[var(--teal-ink)] focus:shadow-[0_0_0_3px_rgba(69,169,160,0.18)]";

export default function Contact({ doc, alternates }) {
  const { t, r, lang } = useLang();
  const c = t.contact;
  useSeo({ title: doc.meta_title || `${c.title} – Translinked`, description: doc.meta_description || c.lede, path: doc.path, lang: doc.lang, alternates });
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "", privacy_ack: false, website: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2) er.name = t.up.err.required;
    if (!/^\S+@\S+\.\S+$/.test(f.email)) er.email = t.up.err.email;
    if (f.message.trim().length < 5) er.message = t.up.err.required;
    if (!f.privacy_ack) er.privacy = t.up.err.privacy;
    setErrors(er);
    if (Object.keys(er).length) return;
    setState("sending");
    try {
      await api.post("/contact", { ...f, lang });
      setState("done");
    } catch (err) {
      setState("idle");
      setMsg(err?.response?.status === 429 ? t.up.err.rate : errDetail(err, t.up.err.generic) === "rejected" ? t.up.err.generic : t.up.err.generic);
    }
  };
  return (
    <main data-testid="contact-page">
      <PageHero eyebrow={c.eyebrow} title={c.title} lede={c.lede} />
      <Container className="grid gap-14 pb-24 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          {state === "done" ? (
            <div className="rounded-sm border border-[var(--line)] bg-white p-10" data-testid="contact-success">
              <CheckCircle2 className="h-10 w-10 text-[var(--teal-ink)]" />
              <p className="mt-6 font-display text-2xl text-[var(--ink)]">{c.success}</p>
            </div>
          ) : (
            <form onSubmit={submit} noValidate className="space-y-6 rounded-sm border border-[var(--line)] bg-white p-6 md:p-10" data-testid="contact-form">
              <div className="grid gap-6 md:grid-cols-2">
                <Field label={c.name} error={errors.name} id="contact-name"><input id="contact-name" className={inputCls} value={f.name} onChange={set("name")} autoComplete="name" data-testid="contact-name-input" /></Field>
                <Field label={c.email} error={errors.email} id="contact-email"><input id="contact-email" type="email" className={inputCls} value={f.email} onChange={set("email")} autoComplete="email" data-testid="contact-email-input" /></Field>
              </div>
              <Field label={c.phone} id="contact-phone"><input id="contact-phone" className={inputCls} value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="contact-phone-input" /></Field>
              <Field label={c.message} error={errors.message} id="contact-message"><textarea id="contact-message" rows={6} className={inputCls} value={f.message} onChange={set("message")} data-testid="contact-message-input" /></Field>
              <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" />
              <label className="flex items-start gap-3 text-sm text-[var(--ink)]">
                <input type="checkbox" checked={f.privacy_ack} onChange={set("privacy_ack")} className="mt-0.5 h-5 w-5 accent-[var(--teal-ink)]" data-testid="contact-privacy-checkbox" />
                <span>{c.privacy} <Link to={r("privacy")} className="underline underline-offset-2" target="_blank">{c.privacyLink}</Link></span>
              </label>
              {errors.privacy && <p className="text-sm text-[#B42318]">{errors.privacy}</p>}
              {msg && <p className="text-sm text-[#B42318]" role="alert" data-testid="contact-error">{msg}</p>}
              <Btn type="submit" disabled={state === "sending"} icon={state === "sending" ? null : "arrow"} data-testid="contact-submit-btn">
                {state === "sending" ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />{c.sending}</span> : c.send}
              </Btn>
            </form>
          )}
          <Link to={r("upload")} className="mt-6 flex items-center justify-between gap-4 rounded-sm bg-[var(--teal)] p-6 text-[var(--ink)] transition-transform hover:-translate-y-0.5" data-testid="contact-upload-link">
            <span><span className="block text-sm">{c.uploadHint}</span><span className="font-display text-xl">{c.uploadLink}</span></span><UploadIcon className="h-6 w-6" />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="space-y-10 lg:col-span-4 lg:col-start-9">
          <div>
            <p className="eyebrow text-[var(--muted)]">{c.details}</p>
            <ul className="mt-4 text-[var(--ink)]">
              {[[Mail, "info@translinked.net", "mailto:info@translinked.net"], [Phone, PHONE, "tel:+491608493791"], [MessageCircle, t.ccta.whatsapp, WA]].map(([I, l, h]) => (
                <li key={l}><a href={h} target={h.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-center gap-3 border-t border-[var(--line)] py-4 hover:text-[var(--teal-ink)]" data-testid={`contact-info-${l.slice(0, 5)}`}><I className="h-4 w-4 text-[var(--teal-ink)]" />{l}</a></li>
              ))}
              <li className="flex gap-3 border-y border-[var(--line)] py-4"><MapPin className="mt-1 h-4 w-4 text-[var(--teal-ink)]" /><span>Translinked<br />Aegidiistr. 35<br />48143 Münster</span></li>
            </ul>
          </div>
          <div>
            <p className="eyebrow text-[var(--muted)]">{c.next}</p>
            <ol className="mt-4 space-y-4">{c.nextSteps.map((s, i) => <li key={s} className="flex gap-4"><span className="font-mono text-xs text-[var(--teal-ink)] pt-1">0{i + 1}</span><span className="text-[var(--ink)]">{s}</span></li>)}</ol>
          </div>
        </Reveal>
      </Container>
    </main>
  );
}
